import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import EventCard from '@/components/feature/EventCard';
import usePageMeta from '@/hooks/usePageMeta';
import { categories, sortOptions } from '@/mocks/categories';
import type { EventItem } from '@/mocks/events';
import { getHotEvents, searchEvents } from '@/utils/news';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const [input, setInput] = useState(q);
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState('latest');

  usePageMeta({
    title: q ? `搜索“${q}” - 每日大瓜网红大赛百度` : '搜索网红大赛资讯 - 每日大瓜网红大赛百度',
    description: q
      ? `关于“${q}”的网红大赛资讯搜索结果，涵盖赛事动态、精彩回顾与相关赛事报道。`
      : '搜索网红大赛资讯、赛事动态与精彩回顾。',
    keywords: `${q || '网红大赛'},网红赛事搜索,每日大瓜,赛事资讯`,
  });

  useEffect(() => {
    setInput(q);
  }, [q]);

  const rawResults = useMemo(() => searchEvents(q), [q]);

  const results = useMemo(() => {
    let list: EventItem[] = [...rawResults];
    if (category) {
      list = list.filter((e) => e.categoryId === category);
    }
    if (sort === 'hot') {
      list.sort((a, b) => b.views - a.views);
    } else {
      list.sort((a, b) => (a.date < b.date ? 1 : -1));
    }
    return list;
  }, [rawResults, category, sort]);

  const hot = getHotEvents(4);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const value = input.trim();
    if (!value) return;
    setSearchParams({ q: value });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <SiteLayout>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10">
        <Breadcrumb items={[{ label: '搜索结果' }]} />

        <header className="mt-4 mb-6">
          <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
            搜索赛事资讯
          </h1>
          <form onSubmit={handleSubmit} className="mt-4 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500"></i>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入赛事名称或关键词…"
                className="w-full pl-9 pr-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:border-accent-400 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
        </header>

        {q && (
          <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 mb-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <p className="text-sm text-foreground-600">
                关于 <span className="font-semibold text-accent-600">“{q}”</span> 共找到{' '}
                <span className="font-semibold text-primary-600">{results.length}</span> 条结果
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-700 cursor-pointer focus:outline-none"
                >
                  <option value="">全部类别</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-700 cursor-pointer focus:outline-none"
                >
                  {sortOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {results.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {results.map((e, i) => (
              <EventCard key={e.id} event={e} index={i} />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-background-200 bg-background-50 py-14 px-6 text-center">
            <span className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-3xl">
              <i className="ri-search-eye-line"></i>
            </span>
            <h3 className="mt-4 font-heading font-bold text-lg text-foreground-950">
              未找到相关内容
            </h3>
            <p className="mt-2 text-sm text-foreground-500">
              {q ? `没有找到与“${q}”匹配的赛事资讯，换个关键词试试，或看看下面的热门推荐。` : '输入关键词开始搜索网红大赛资讯。'}
            </p>
            {q && (
              <button
                type="button"
                onClick={() => {
                  setInput('');
                  setSearchParams({});
                }}
                className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md border border-background-300 text-sm font-medium text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                重置搜索
              </button>
            )}

            <div className="mt-10 text-left">
              <h4 className="font-heading font-bold text-base text-foreground-950 mb-4 text-center">
                热门赛事推荐
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {hot.map((e, i) => (
                  <EventCard key={e.id} event={e} index={i} />
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 text-center">
          <Link
            to="/category/all"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-600 hover:text-accent-700 transition-colors whitespace-nowrap"
          >
            浏览全部赛事资讯
            <i className="ri-arrow-right-s-line"></i>
          </Link>
        </div>
      </div>
    </SiteLayout>
  );
}